import React, { useState, useEffect } from 'react';
import { Switch, Card, Space, Typography, Button, message } from 'antd';
import { EnvironmentOutlined, ApiOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { Environment } from '../types';
import './EnvironmentSwitcher.css';

/**
 * 环境切换组件
 * 允许用户在本地API和线上API之间切换
 */
const EnvironmentSwitcher: React.FC = () => {
  const [env, setEnv] = useState<Environment>('production');
  const [loading, setLoading] = useState(false);

  // 从localStorage读取保存的环境
  useEffect(() => {
    const savedEnv = localStorage.getItem('admin_env') as Environment;
    if (savedEnv && (savedEnv === 'development' || savedEnv === 'production')) {
      setEnv(savedEnv);
    }
  }, []);

  const envOptions = [
    {
      key: 'development' as Environment,
      label: '本地开发环境',
      description: '连接本地开发服务器',
      url: 'http://localhost:3001/api/v1',
      icon: <EnvironmentOutlined />,
      color: '#52c41a',
    },
    {
      key: 'production' as Environment,
      label: '线上生产环境',
      description: '连接线上生产服务器',
      url: 'https://xcx.shoudian.xin/api/v1',
      icon: <ApiOutlined />,
      color: '#1890ff',
    },
  ];

  const handleEnvChange = (checked: boolean, newEnv: Environment) => {
    setEnv(newEnv);
  };

  const handleSwitch = async () => {
    if (loading) return;

    setLoading(true);

    try {
      // 保存环境配置
      localStorage.setItem('admin_env', env);
      
      // 清除本地token，重新登录
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      localStorage.removeItem('admin_user');

      // 提示用户需要重新登录
      message.success({
        content: `已切换到${envOptions.find(e => e.key === env)?.label}，请重新登录`,
        duration: 3,
      });

      // 延迟跳转到登录页
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    } catch (error) {
      message.error('环境切换失败，请重试');
      setLoading(false);
    }
  };

  const currentEnvConfig = envOptions.find(e => e.key === env);

  return (
    <Card style={{ marginBottom: 24 }}>
      <Space direction="vertical" size="large">
        <div>
          <Typography.Title level={5}>API 环境设置</Typography.Title>
          <Typography.Text type="secondary">
            当前使用 {currentEnvConfig?.label}
          </Typography.Text>
        </div>

        {envOptions.map((option) => (
          <Card
            key={option.key}
            className={`env-card ${env === option.key ? 'active' : ''}`}
            onClick={() => handleEnvChange(true, option.key)}
          >
            <div className="env-left">
              <div className="env-icon-wrapper">
                {option.icon}
              </div>
              <div className="env-info">
                <Typography.Text strong>{option.label}</Typography.Text>
                <Typography.Text type="secondary">{option.description}</Typography.Text>
              </div>
            </div>
            <div className="env-right">
              <Switch
                checked={env === option.key}
                onChange={(checked) => handleEnvChange(checked, option.key)}
                disabled={loading}
              />
            </div>
          </Card>
        ))}

        <div className="env-actions">
          <Button
            type="primary"
            size="large"
            onClick={handleSwitch}
            loading={loading}
            disabled={env === 'production'}
          >
            切换环境
          </Button>
        </div>

        <div className="env-info">
          <Typography.Text type="secondary">
            <InfoCircleOutlined /> 切换环境会清除登录状态，请确保您在对应的环境中完成登录
          </Typography.Text>
        </div>
      </Space>
    </Card>
  );
};

export default EnvironmentSwitcher;